import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { assetUrl } from "../api";
import { filterFiles, isAttachmentFile, MAX_ATTACHMENTS, uploadErrorMessage } from "../uploadUtils";
import AdminLayout from "../components/AdminLayout";
import {
  ChevronRight, Eye, Trash2, Plus, UploadCloud, Send, FileText, X, AlertTriangle,
  Image as ImageIcon,
} from "lucide-react";
import {
  money, fmtDate, partLine, computeQuotePricing, DEFAULT_TAX_RATE, QUOTE_VALID_DAYS, ADMIN_QUOTE_STATUS,
  numberVersions,
} from "../quoteUtils";

const blankPart = (req) => ({
  name: req?.partRequired || "",
  partNumber: req?.partNumber || "",
  vehicle: "",
  stock: "",
  location: "",
  km: "",
  image: req?.partPhotos?.[0] || "",
  condition: "Used",
  quantity: 1,
  unitPrice: 0,
  coreCharge: 0,
  freight: 0,
});

// keep only the fields the Quotation schema stores
const cleanPart = (p) => ({
  name: p.name || "",
  partNumber: p.partNumber || "",
  condition: p.condition || "Used",
  quantity: Number(p.quantity) || 1,
  unitPrice: Number(p.unitPrice) || 0,
  coreCharge: Number(p.coreCharge) || 0,
  freight: Number(p.freight) || 0,
  vehicle: p.vehicle || "",
  stock: p.stock || "",
  location: p.location || "",
  km: p.km || "",
  image: p.image || "",
});

// const WARRANTIES = ["3 Months / 5,000 km", "6 Months / 10,000 km", "12 Months / 20,000 km", "No Warranty"];
const WARRANTIES = ["3 months parts only", "6 months parts only"];
const DELIVERIES = ["Standard Delivery", "Express Delivery", "Pickup / Collection"];

export default function CreateQuote() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [req, setReq] = useState(null);
  const [existingQuote, setExistingQuote] = useState(null); // latest saved version (any status)
  const [loadError, setLoadError] = useState("");
  const [parts, setParts] = useState([]);
  const [warranty, setWarranty] = useState(WARRANTIES[0]);
  const [delivery, setDelivery] = useState(DELIVERIES[0]);
  const [estDelivery, setEstDelivery] = useState("");
  const [customerNote, setCustomerNote] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [attachments, setAttachments] = useState([]); // new File[]
  const [existingAttachments, setExistingAttachments] = useState([]); // already uploaded {url,name,type,size}
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [taxRate, setTaxRate] = useState(DEFAULT_TAX_RATE); // GST from Settings → Company, falls back to 10%
  const fileRef = useRef(null);

  // ---------- load request + latest quotation, then prefill ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: request } = await api.get(`/requests/${id}`);
        const { data: list } = await api.get(`/requests/${id}/quotations`);
        const numbered = numberVersions(Array.isArray(list) ? list : []);
        const quote = numbered.find((q) => q.isLatest) || numbered[0] || null;
        if (cancelled) return;

        setReq(request);
        setExistingQuote(quote);

        if (quote) {
          setParts(quote.parts?.length ? quote.parts.map(cleanPart) : [blankPart(request)]);
          setWarranty(quote.warranty || WARRANTIES[0]);
          setDelivery(quote.deliveryType || DELIVERIES[0]);
          setEstDelivery(quote.estDelivery || "");
          setCustomerNote(quote.customerNote || "");
          setInternalNote(quote.internalNote || "");
          setExistingAttachments(quote.attachments || []);
        } else {
          setParts([blankPart(request)]);
          setInternalNote(request.quoteInternalNote || "");
        }
      } catch (e) {
        if (!cancelled) setLoadError(e.response?.data?.message || "Could not load this request.");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Use the GST rate configured in Settings so the preview matches what is saved.
  useEffect(() => {
    api.get("/admin/settings/company")
      .then(({ data }) => {
        const rate = Number(data?.gstRate);
        if (Number.isFinite(rate) && rate >= 0 && rate <= 100) setTaxRate(rate);
      })
      .catch(() => { /* keep the default */ });
  }, []);

  const setPart = (i, key, val) =>
    setParts((prev) => prev.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));
  const addPart = () => setParts((prev) => [...prev, blankPart(req)]);
  const removePart = (i) => setParts((prev) => prev.filter((_, idx) => idx !== i));

  const pricing = useMemo(() => computeQuotePricing(parts, taxRate), [parts, taxRate]);

  const addFiles = (list) => {
    const room = MAX_ATTACHMENTS - existingAttachments.length - attachments.length;
    const { accepted, problems } = filterFiles(list, { room, accept: isAttachmentFile });
    if (problems.length) alert(problems.join("\n"));
    if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
  };

  const isEditing = !!existingQuote;
  const editingDraft = existingQuote?.status === "draft";
  // Editing a draft updates it; editing anything the customer has seen creates the next version.
  const targetVersion = !existingQuote ? 1 : editingDraft ? existingQuote._v : existingQuote._v + 1;
  const wasSent = !!req?.quotedAt;
  const status = req?.quoteStatus || "pending";

  const validate = (sending) => {
    if (!sending) return "";
    if (parts.length === 0) return "Add at least one part before sending.";
    const missing = parts.findIndex((p) => !String(p.name).trim());
    if (missing >= 0) return `Part #${missing + 1} needs a name.`;
    const noPrice = parts.findIndex((p) => !(Number(p.unitPrice) > 0));
    if (noPrice >= 0) return `Part #${noPrice + 1} needs a unit price above $0.`;
    return "";
  };

  const submit = async (sending) => {
    const problem = validate(sending);
    if (problem) return alert(problem);
    if (saving) return;

    if (sending && status === "approved" &&
      !window.confirm("The customer already approved the current quote. Sending a new version replaces it and they will need to approve again. Continue?")) return;

    setSaving(true);
    const fd = new FormData();
    fd.append("parts", JSON.stringify(parts.map(cleanPart)));
    fd.append("warranty", warranty);
    fd.append("delivery", delivery);
    fd.append("estDelivery", estDelivery);
    fd.append("customerNote", customerNote);
    fd.append("internalNote", internalNote);
    fd.append("existingAttachments", JSON.stringify(existingAttachments));
    attachments.forEach((f) => fd.append("quoteAttachments", f));

    try {
      await api.post(`/requests/${id}/quote${sending ? "?send=true" : ""}`, fd);
      alert(sending ? "Quote sent to customer." : "Draft saved.");
      navigate(`/admin/requests/${id}`);
    } catch (e) {
      alert(uploadErrorMessage(e, "Could not save quote."));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return <AdminLayout><main className="p-6"><div className="bg-white rounded-xl p-12 text-center text-red-500">{loadError}</div></main></AdminLayout>;
  }
  if (!req) {
    return <AdminLayout><main className="p-6"><div className="bg-white rounded-xl p-12 text-center text-slate-400">Loading…</div></main></AdminLayout>;
  }

  const statusMeta = ADMIN_QUOTE_STATUS[status] || ADMIN_QUOTE_STATUS.pending;

  return (
    <AdminLayout>
      <main className="p-6 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <button onClick={() => navigate("/admin/requests")} className="hover:text-slate-700">Requests</button>
              <ChevronRight size={14} />
              <button onClick={() => navigate(`/admin/requests/${id}`)} className="hover:text-slate-700">{req.requestId}</button>
              <ChevronRight size={14} />
              <span className="text-slate-700">{isEditing ? `Edit Quote ${existingQuote._v}` : "Create Quote"}</span>
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <h1 className="text-3xl font-extrabold text-slate-800">
                {!isEditing ? "Create Quote" : editingDraft ? `Edit draft Quote ${existingQuote._v}` : `New version: Quote ${targetVersion}`}
              </h1>
              {isEditing && (
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${statusMeta.cls}`}>{statusMeta.label}</span>
              )}
            </div>
            <p className="text-slate-500">
              {!isEditing
                ? "Add part, price and send quote to customer."
                : editingDraft
                  ? `Quote ${existingQuote._v} hasn't been sent yet, so saving updates it.`
                  : `Started from Quote ${existingQuote._v}. Saving creates Quote ${targetVersion}; Quote ${existingQuote._v} stays on record so you can compare what was shared before.`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/admin/requests/${id}`)}
              className="px-5 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-800 text-white font-semibold text-sm hover:bg-slate-900">
              <Eye size={16} /> Preview Quote
            </button>
          </div>
        </div>

        {/* Context banners */}
        {status === "revision_requested" && (
          <div className="flex items-start gap-3 bg-purple-50 text-purple-800 rounded-xl px-4 py-3 text-sm">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>The customer asked for a revision. Check the comments on the request, update the quote and send it again.</div>
          </div>
        )}
        {isEditing && wasSent && status !== "draft" && (
          <div className="flex items-start gap-3 bg-amber-50 text-amber-800 rounded-xl px-4 py-3 text-sm">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              The customer currently sees Quote {existingQuote._v}, sent {fmtDate(req.quotedAt)}.
              <b> Save as Draft</b> saves Quote {targetVersion} privately (the customer sees "quote being updated").
              <b> Send</b> shares Quote {targetVersion} with them now.
            </div>
          </div>
        )}
        {status === "draft" && wasSent && (
          <div className="flex items-start gap-3 bg-slate-100 text-slate-700 rounded-xl px-4 py-3 text-sm">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>You're editing an unsent draft. The customer still sees the quote sent on {fmtDate(req.quotedAt)} until you send this one.</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT (2 cols) */}
          <div className="lg:col-span-2 space-y-6">
            {parts.map((p, i) => {
              const line = partLine(p, taxRate);
              return (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-800">Part Details {parts.length > 1 ? `#${i + 1}` : ""}</h3>
                    {parts.length > 1 && (
                      <button onClick={() => removePart(i)} aria-label={`Remove part ${i + 1}`}
                        className="text-red-500 border border-red-100 rounded-lg p-2 hover:bg-red-50">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <div className="w-32 h-28 rounded-xl bg-slate-100 overflow-hidden shrink-0 grid place-items-center">
                      {p.image ? <img src={assetUrl(p.image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="text-slate-300" />}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <input value={p.name} onChange={(e) => setPart(i, "name", e.target.value)}
                          className="font-bold text-slate-800 text-lg border-b border-gray-200 focus:border-slate-500 focus:outline-none flex-1 min-w-[180px]"
                          placeholder="Part name" />
                        <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded uppercase">{p.condition}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input value={p.partNumber} onChange={(e) => setPart(i, "partNumber", e.target.value)}
                          className="text-sm text-slate-600 border-b border-gray-100 focus:border-slate-400 focus:outline-none" placeholder="Part number" />
                        <input value={p.vehicle} onChange={(e) => setPart(i, "vehicle", e.target.value)}
                          className="text-sm text-slate-600 border-b border-gray-100 focus:border-slate-400 focus:outline-none" placeholder="Vehicle (e.g. Toyota Hilux 2018)" />
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                        <label>STOCK: <input value={p.stock} onChange={(e) => setPart(i, "stock", e.target.value)} className="font-semibold text-slate-700 w-24 border-b border-gray-100 focus:outline-none" placeholder="MEL-100234" /></label>
                        <label>LOCATION: <input value={p.location} onChange={(e) => setPart(i, "location", e.target.value)} className="font-semibold text-slate-700 w-24 border-b border-gray-100 focus:outline-none" placeholder="Melbourne" /></label>
                        <label>KM: <input value={p.km} onChange={(e) => setPart(i, "km", e.target.value)} className="font-semibold text-slate-700 w-24 border-b border-gray-100 focus:outline-none" placeholder="98,000 km" /></label>
                      </div>
                      {req.partPhotos?.length > 1 && (
                        <div className="flex items-center gap-2 pt-1">
                          <span className="text-xs text-slate-400">Photo:</span>
                          {req.partPhotos.map((ph) => (
                            <button key={ph} onClick={() => setPart(i, "image", ph)}
                              className={`w-9 h-9 rounded overflow-hidden border-2 ${p.image === ph ? "border-blue-500" : "border-transparent"}`}>
                              <img src={assetUrl(ph)} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-5">
                    <label className="text-sm">
                      <span className="text-xs font-semibold text-slate-400 uppercase">Condition</span>
                      <select value={p.condition} onChange={(e) => setPart(i, "condition", e.target.value)}
                        className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm">
                        {["Used", "New", "Reconditioned", "Refurbished"].map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-semibold text-slate-400 uppercase">Quantity</span>
                      <input type="number" min="1" value={p.quantity} onChange={(e) => setPart(i, "quantity", e.target.value)}
                        className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm" />
                    </label>
                  </div>

                  {/* <h4 className="font-bold text-slate-800 mt-6 mb-3">Pricing</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label className="text-sm">
                      <span className="text-xs font-semibold text-slate-400 uppercase">Unit Price (AUD, ex GST)</span>
                      <input type="number" min="0" step="0.01" value={p.unitPrice} onChange={(e) => setPart(i, "unitPrice", e.target.value)}
                        className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="0.00" />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-semibold text-slate-400 uppercase">Core Charge (Refundable)</span>
                      <input type="number" min="0" step="0.01" value={p.coreCharge} onChange={(e) => setPart(i, "coreCharge", e.target.value)}
                        className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="0.00" />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-semibold text-slate-400 uppercase">Freight / Delivery (AUD)</span>
                      <input type="number" min="0" step="0.01" value={p.freight} onChange={(e) => setPart(i, "freight", e.target.value)}
                        className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="0.00" />
                    </label>
                    <label className="text-sm">
                      <span className="text-xs font-semibold text-slate-400 uppercase">GST ({taxRate}% on part + freight)</span>
                      <input readOnly value={line.tax.toFixed(2)}
                        className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-slate-50" />
                    </label>
                    <div className="sm:col-span-2 text-sm">
                      <span className="text-xs font-semibold text-slate-400 uppercase">Line Total (inc. GST &amp; core)</span>
                      <div className="w-full mt-1 px-3 py-2.5 border border-gray-100 rounded-lg bg-slate-50 font-bold text-xl text-slate-800">
                        {money(line.total)}
                      </div>
                    </div>
                  </div> */}


<h4 className="font-bold text-slate-800 mt-6 mb-3">
  Pricing
</h4>

<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  {/* Unit Price */}
  <label className="text-sm">
    <span className="text-xs font-semibold text-slate-400 uppercase">
      Unit Price (Included GST)
    </span>

    <input
      type="number"
      min="0"
      step="0.01"
      value={p.unitPrice}
      onChange={(e) => setPart(i, "unitPrice", e.target.value)}
      className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
      placeholder="0.00"
    />
  </label>

  {/* Delivery / Collection */}
  <label className="text-sm">
    <span className="text-xs font-semibold text-slate-400 uppercase">
      Delivery / Collection
    </span>

    <select
      value={delivery}
      onChange={(e) => setDelivery(e.target.value)}
      className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
    >
      {DELIVERIES.map((d) => (
        <option key={d} value={d}>
          {d}
        </option>
      ))}
    </select>
  </label>

  {/* Freight */}
  <label className="text-sm">
    <span className="text-xs font-semibold text-slate-400 uppercase">
      Freight / Delivery (AUD)
    </span>

    <input
      type="number"
      min="0"
      step="0.01"
      value={p.freight}
      onChange={(e) => setPart(i, "freight", e.target.value)}
      className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
      placeholder="0.00"
    />
  </label>
</div>

<div className="mt-4">
  <span className="text-xs font-semibold text-slate-400 uppercase">
    Total (AUD)
  </span>

  <div className="w-full mt-1 px-3 py-3 border border-gray-100 rounded-lg bg-slate-50 font-bold text-xl text-slate-800">
    {money(
      (Number(p.unitPrice) || 0) * (Number(p.quantity) || 1) +
      (Number(p.freight) || 0)
    )}
  </div>
</div>


                </div>
              );
            })}

            <button onClick={addPart} className="flex items-center gap-2 text-blue-600 font-semibold text-sm">
              <Plus size={16} /> Add Another Part
            </button>

            {/* Quote details */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-bold text-slate-800 mb-4">Quote Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Warranty</span>
                  <select value={warranty} onChange={(e) => setWarranty(e.target.value)}
                    className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm">
                    {[...new Set([warranty, ...WARRANTIES])].map((w) => <option key={w}>{w}</option>)}
                  </select>
                </label>
                {/* <label className="text-sm">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Delivery / Collection</span>
                  <select value={delivery} onChange={(e) => setDelivery(e.target.value)}
                    className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm">
                    {[...new Set([delivery, ...DELIVERIES])].map((d) => <option key={d}>{d}</option>)}
                  </select>
                </label> */}
                <label className="text-sm">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Estimated Delivery</span>
                  <input type="date" value={estDelivery} onChange={(e) => setEstDelivery(e.target.value)}
                    className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm" />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <label className="text-sm">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Customer Note (visible on quote)</span>
                  <textarea value={customerNote} onChange={(e) => setCustomerNote(e.target.value)} rows={3}
                    className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                    placeholder="Quality tested engine. Warranty covers parts only." />
                </label>
                <label className="text-sm">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Internal Note (not visible to customer)</span>
                  <textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={3}
                    className="w-full mt-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm"
                    placeholder="Customer prefers low km engine. Call before dispatch." />
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <button onClick={() => submit(true)} disabled={saving}
                className="flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-800 text-white font-semibold text-sm hover:bg-teal-900 disabled:opacity-60">
                <Send size={16} /> {saving ? "Saving…" : targetVersion > 1 ? `Send Quote ${targetVersion} to Customer` : "Send Quote to Customer"}
              </button>
              <button onClick={() => submit(false)} disabled={saving}
                className="flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                <FileText size={16} /> {editingDraft ? "Update Draft" : "Save as Draft"}
              </button>
            </div>
          </div>

          {/* RIGHT — summary + attachments */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-bold text-slate-800 mb-4">Quote Summary</h3>
              <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-100">
                <div>
                  <div className="text-[11px] font-semibold text-slate-400 uppercase">Request ID</div>
                  <div className="font-bold text-slate-800">{req.requestId}</div>
                  {existingQuote && <div className="text-xs text-slate-400">{existingQuote.quoteId}</div>}
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-slate-400 uppercase">Customer</div>
                  <div className="font-bold text-slate-800">{req.customerName}</div>
                  <div className="text-xs text-slate-400">{req.customerContact}</div>
                </div>
              </div>

              <SummaryLines parts={parts} pricing={pricing} />

              <div className="flex items-center gap-2 bg-amber-50 text-amber-700 text-sm rounded-lg px-3 py-2">
                <span>⚠</span> This quote is valid for {QUOTE_VALID_DAYS} days.
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-bold text-slate-800 mb-1">Attachments <span className="text-slate-400 font-normal text-sm">(Optional)</span></h3>
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileRef.current?.click()}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                className={`mt-3 cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition ${dragging ? "border-blue-400 bg-blue-50/50" : "border-gray-200"}`}
              >
                <UploadCloud className="mx-auto text-slate-400" />
                <p className="text-sm text-slate-500 mt-2">Drag &amp; drop files here or</p>
                <span className="inline-block mt-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-slate-700">Upload Files</span>
                <input ref={fileRef} type="file" multiple hidden accept="image/*,application/pdf,audio/*,video/*"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              </div>
              <p className="text-xs text-slate-400 mt-2 text-center">Images, PDF, audio or video. Max 25 MB each, 10 per save.</p>

              {(existingAttachments.length > 0 || attachments.length > 0) && (
                <div className="mt-4 space-y-2">
                  {existingAttachments.map((a, i) => (
                    <div key={`e${i}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2">
                      <FileText size={14} className="text-slate-400" />
                      <a href={assetUrl(a.url)} target="_blank" rel="noreferrer" className="flex-1 truncate hover:underline">{a.name}</a>
                      <span className="text-[10px] text-slate-400">saved</span>
                      <button aria-label={`Remove ${a.name}`} onClick={() => setExistingAttachments((prev) => prev.filter((_, idx) => idx !== i))}><X size={14} /></button>
                    </div>
                  ))}
                  {attachments.map((f, i) => (
                    <div key={`n${i}`} className="flex items-center gap-2 text-sm bg-blue-50/60 rounded-lg px-3 py-2">
                      <FileText size={14} className="text-slate-400" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-[10px] text-blue-600">new</span>
                      <button aria-label={`Remove ${f.name}`} onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {showPreview && (
          <PreviewModal
            onClose={() => setShowPreview(false)}
            req={req}
            quoteId={existingQuote?.quoteId}
            parts={parts}
            pricing={pricing}
            warranty={warranty}
            delivery={delivery}
            estDelivery={estDelivery}
            customerNote={customerNote}
            attachmentCount={existingAttachments.length + attachments.length}
          />
        )}
      </main>
    </AdminLayout>
  );
}

function SummaryLines({ parts, pricing }) {
  return (
    <>
      <div className="py-4 space-y-2 border-b border-gray-100">
        {parts.map((p, i) => (
          <div key={i} className="flex justify-between text-sm gap-3">
            <div>
              <div className="font-semibold text-slate-700">{p.name || "Part"} {Number(p.quantity) > 1 && <span className="text-slate-400">× {p.quantity}</span>}</div>
              {p.stock && <div className="text-[11px] text-slate-400">STOCK: {p.stock}</div>}
            </div>
            <div className="font-bold text-slate-800">{money((Number(p.quantity) || 1) * (Number(p.unitPrice) || 0))}</div>
          </div>
        ))}
      </div>
      <div className="py-4 space-y-2 text-sm border-b border-gray-100">
        <div className="flex justify-between text-slate-500"><span>Parts subtotal</span><span>{money(pricing.subtotal)}</span></div>
        <div className="flex justify-between text-slate-500"><span>Freight / Delivery</span><span>{money(pricing.freightTotal)}</span></div>
        <div className="flex justify-between text-slate-500"><span>GST ({pricing.taxRate}%)</span><span>{money(pricing.tax)}</span></div>
        <div className="flex justify-between text-slate-500"><span>Core Charge (Refundable)</span><span>{money(pricing.coreChargeTotal)}</span></div>
      </div>
      <div className="flex justify-between items-center py-4">
        <span className="font-bold text-slate-800">Total (AUD)</span>
        <span className="text-2xl font-extrabold text-teal-800">{money(pricing.total)}</span>
      </div>
    </>
  );
}

// What the customer will see — built from the form values, nothing is saved
function PreviewModal({ onClose, req, quoteId, parts, pricing, warranty, delivery, estDelivery, customerNote, attachmentCount }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Quote preview"
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs text-slate-400">Customer preview</div>
            <h2 className="text-2xl font-extrabold text-slate-800">{quoteId || "New quote"}</h2>
            <div className="text-sm text-slate-500">{req.requestId} · {req.customerName}</div>
          </div>
          <button onClick={onClose} aria-label="Close preview" className="p-2 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="mt-5 divide-y divide-gray-100 border-y border-gray-100">
          {parts.map((p, i) => (
            <div key={i} className="flex items-center gap-4 py-3">
              <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden grid place-items-center shrink-0">
                {p.image ? <img src={assetUrl(p.image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-slate-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800">{p.name || "Part"}</div>
                <div className="text-xs text-slate-500">
                  {[p.condition, p.vehicle, p.partNumber && `#${p.partNumber}`, p.km].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="text-right text-sm">
                <div className="font-semibold">{money((Number(p.quantity) || 1) * (Number(p.unitPrice) || 0))}</div>
                <div className="text-xs text-slate-400">Qty {p.quantity}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm mt-4">
          <div><span className="text-slate-400">Warranty:</span> {warranty || "—"}</div>
          <div><span className="text-slate-400">Delivery:</span> {delivery || "—"}{estDelivery ? ` (est. ${fmtDate(estDelivery)})` : ""}</div>
          <div><span className="text-slate-400">Attachments:</span> {attachmentCount}</div>
        </div>
        {customerNote && <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-line">{customerNote}</p>}

        <SummaryLines parts={parts} pricing={pricing} />
      </div>
    </div>
  );
}
